import React, { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, BookOpen, Printer, Download } from "lucide-react";

const MANUALS = {
    cliente: {
        slug: "cliente",
        file: "/manuals/cliente.md",
        title: "Manuale Cliente · TotalPL",
        subtitle: "Come funziona TotalPL dal tuo punto di vista",
        backLink: "/totalpl",
        backLabel: "Torna a TotalPL",
        accent: "#FF1A5C",
    },
    tipografia: {
        slug: "tipografia",
        file: "/manuals/tipografia.md",
        title: "Manuale Operatore · TotalPL",
        subtitle: "Guida operativa completa per la tua tipografia",
        backLink: "/dashboard",
        backLabel: "Torna alla dashboard",
        accent: "#FF6B1A",
    },
};

export default function ManualPage() {
    const { kind } = useParams();
    const manual = MANUALS[kind];
    const [content, setContent] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!manual) return;
        setLoading(true);
        fetch(manual.file)
            .then(r => r.text())
            .then(t => { setContent(t); setLoading(false); })
            .catch(() => { setContent("Errore nel caricamento del manuale."); setLoading(false); });
    }, [manual]);

    if (!manual) return <Navigate to="/" replace />;

    return (
        <div style={{ fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif', background: "#F8FAFC", color: "#0A1730", minHeight: "100vh" }}>
            {/* Header */}
            <header className="px-6 md:px-12 py-4 flex items-center justify-between sticky top-0 z-40 backdrop-blur-md" style={{ background: "rgba(255,255,255,0.95)", borderBottom: "1px solid #E6ECFA" }}>
                <Link to={manual.backLink} className="flex items-center gap-2 text-[13px] font-bold hover:opacity-70" data-testid="manual-back">
                    <ArrowLeft size={14} /> <span>{manual.backLabel}</span>
                </Link>
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full" style={{ background: `${manual.accent}15`, color: manual.accent }}>
                    <BookOpen size={13} />
                    <span className="mono text-[10.5px] uppercase tracking-[0.15em] font-extrabold">{kind === "tipografia" ? "Operatore" : "Cliente"}</span>
                </div>
                <button
                    type="button"
                    onClick={() => window.print()}
                    className="hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-extrabold hover:opacity-80"
                    style={{ background: "#0A1730", color: "#fff" }}
                    data-testid="manual-print"
                >
                    <Printer size={12} /> Stampa / Salva PDF
                </button>
            </header>

            {/* Hero */}
            <section className="px-6 md:px-12 py-12 md:py-16" style={{ background: `linear-gradient(135deg, ${manual.accent}10, ${manual.accent}03)` }}>
                <div className="max-w-3xl mx-auto">
                    <h1 className="text-[34px] md:text-[46px] font-extrabold tracking-tighter leading-[1.05]" data-testid="manual-title">{manual.title}</h1>
                    <p className="mt-3 text-[15px] md:text-[17px]" style={{ color: "#5C6B8C" }}>{manual.subtitle}</p>
                </div>
            </section>

            {/* Content */}
            <section className="px-6 md:px-12 py-12 md:py-16">
                <article className="max-w-3xl mx-auto manual-prose" data-testid="manual-content">
                    {loading ? (
                        <div className="text-center opacity-60 py-16">Caricamento...</div>
                    ) : (
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
                    )}
                </article>
            </section>

            {/* Footer */}
            <footer className="px-6 md:px-12 py-10 text-center" style={{ background: "#0A1730", color: "#94A3B8" }}>
                <div className="text-[12px] opacity-80">© {new Date().getFullYear()} TotalKlick srls · Tutti i diritti riservati</div>
                <div className="mt-2 flex items-center justify-center gap-4 text-[11px]">
                    <Link to="/legal/privacy" className="hover:text-white">Privacy</Link>
                    <Link to="/legal/terms" className="hover:text-white">Termini</Link>
                    <a href="mailto:support@totalklick.it" className="hover:text-white">Supporto</a>
                </div>
            </footer>

            {/* Markdown styling */}
            <style>{`
                .manual-prose h1 { font-size: 30px; font-weight: 900; letter-spacing: -0.02em; margin: 2.5rem 0 1rem; color: #0A1730; }
                .manual-prose h2 { font-size: 24px; font-weight: 800; letter-spacing: -0.015em; margin: 2.25rem 0 0.875rem; color: #0A1730; padding-bottom: 0.5rem; border-bottom: 2px solid ${manual.accent}30; }
                .manual-prose h3 { font-size: 18px; font-weight: 800; margin: 1.5rem 0 0.625rem; color: #0A1730; }
                .manual-prose h4 { font-size: 15px; font-weight: 800; margin: 1rem 0 0.5rem; color: ${manual.accent}; }
                .manual-prose p { font-size: 15px; line-height: 1.7; margin: 0.875rem 0; color: #1F2A40; }
                .manual-prose ul, .manual-prose ol { padding-left: 1.5rem; margin: 0.875rem 0; }
                .manual-prose li { font-size: 15px; line-height: 1.7; margin: 0.25rem 0; color: #1F2A40; }
                .manual-prose ul > li::marker { color: ${manual.accent}; }
                .manual-prose ol > li::marker { color: ${manual.accent}; font-weight: 800; }
                .manual-prose strong { font-weight: 800; color: #0A1730; }
                .manual-prose em { color: #5C6B8C; }
                .manual-prose code { background: #F1F5F9; padding: 0.1em 0.4em; border-radius: 4px; font-size: 0.92em; color: #FF1A5C; font-family: "JetBrains Mono", monospace; }
                .manual-prose pre { background: #0E1B2D; color: #E5E7EB; padding: 1rem 1.25rem; border-radius: 10px; overflow-x: auto; font-size: 13px; line-height: 1.6; margin: 1rem 0; }
                .manual-prose pre code { background: transparent; color: inherit; padding: 0; font-size: inherit; }
                .manual-prose blockquote { border-left: 4px solid ${manual.accent}; background: ${manual.accent}08; padding: 0.875rem 1.25rem; border-radius: 8px; margin: 1rem 0; font-size: 14.5px; color: #1F2A40; }
                .manual-prose blockquote p { margin: 0; }
                .manual-prose a { color: ${manual.accent}; font-weight: 700; text-decoration: underline; text-decoration-thickness: 1.5px; text-underline-offset: 3px; }
                .manual-prose a:hover { opacity: 0.7; }
                .manual-prose hr { border: 0; border-top: 1px solid #E6ECFA; margin: 2.5rem 0; }
                .manual-prose table { width: 100%; border-collapse: collapse; margin: 1rem 0; font-size: 14px; }
                .manual-prose th, .manual-prose td { padding: 0.625rem 0.875rem; text-align: left; border-bottom: 1px solid #E6ECFA; }
                .manual-prose th { background: #F1F5F9; font-weight: 800; }
                @media print {
                    header, footer { display: none; }
                    .manual-prose { max-width: 100%; }
                    body { background: #fff; }
                }
            `}</style>
        </div>
    );
}
